import { useEffect, useState } from 'react';
import { Box, Flex, Heading, SimpleGrid, Text, Badge, Divider, Wrap, WrapItem } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { CustomToast } from '../../../../components';
import { useFetch } from '../../../../hooks/useFetch';
import * as routesNames from '../../../../constants/routes';

interface AssetSummary {
    pendingRequestsCount: number;
    requestsReceivedToday: number;
    returnsExpectedToday: number;
    returnsApproachingDue: number;
    todaysRequesters: { employeeName: string; assetType?: string }[];
}

interface StatProps {
    label: string;
    value: number;
    color: string;
    bg: string;
    onClick: () => void;
}

const Stat = ({ label, value, color, bg, onClick }: StatProps) => (
    <Box as="button" textAlign="left" bg={bg} borderRadius="12px" p={3} cursor="pointer" transition="all 0.15s" _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }} onClick={onClick} >
        <Text fontSize="xs" color="gray.600" fontWeight="medium">{label}</Text>
        <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
    </Box>
);

const AssetSummaryCard = () => {
    const navigate = useNavigate();
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [summary, setSummary] = useState<AssetSummary | null>(null);

    const load = async () => {
        // Backend returns all-company or branch-scoped data based on the user's permission
        const response = await fetchApi('Asset/asset-activity-summary', 'GET');
        if (response) setSummary(response);
    };

    useEffect(() => {
        load();
        const interval = setInterval(load, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    if (!summary) return null;

    const goRequests = () => navigate(routesNames.ASSETASSIGNMENT);
    const goReturns = () => navigate(routesNames.ASSETRETURN);

    const requesters = summary.todaysRequesters ?? [];

    return (
        <Box bg="white" borderRadius="14px" boxShadow="sm" p={4}>
            <Flex justify="space-between" align="center" mb={3}>
                <Heading as="h3" size="sm" className="font-poppins">Asset Activity Today</Heading>
                <Badge colorScheme="blue" borderRadius="full" px={2}>
                    Pending Asset Requests: {summary.pendingRequestsCount}
                </Badge>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                <Stat label="Pending Requests" value={summary.pendingRequestsCount} color="#2563eb" bg="#eff6ff" onClick={goRequests} />
                {/* <Stat label="Received Today" value={summary.requestsReceivedToday} color="#7c3aed" bg="#f5f3ff" onClick={goRequests} /> */}
                <Stat label="Returns Due Today" value={summary.returnsExpectedToday} color="#dc2626" bg="#fef2f2" onClick={goReturns} />
                <Stat label="Approaching Return" value={summary.returnsApproachingDue} color="#d97706" bg="#fffbeb" onClick={goReturns} />
            </SimpleGrid>

            {requesters.length > 0 && (
                <>
                    <Divider my={3} />
                    <Text fontSize="xs" color="gray.500" mb={2} fontWeight="medium">Requested today by</Text>
                    <Wrap spacing={2}>
                        {requesters.map((r, i) => (
                            <WrapItem key={`${r.employeeName}-${i}`}>
                                <Badge
                                    cursor="pointer" borderRadius="full" px={2.5} py={1}
                                    colorScheme="gray" textTransform="none" onClick={goRequests}
                                >
                                    {r.employeeName}{r.assetType ? ` · ${r.assetType}` : ''}
                                </Badge>
                            </WrapItem>
                        ))}
                    </Wrap>
                </>
            )}
        </Box>
    );
};

export default AssetSummaryCard;